import React from "react";

// Source
import StoreRole from "../../store/Role";
import StoreRoute from "../../store/Route";
import StoreLocalization from "../../store/Localization";
import StorePreference from "../../store/Preference";
import * as HelperSrc from "../../HelperSrc";
import * as ModelRole from "../../model/Role";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelLocalization from "../../model/Localization";
import * as ModelCustomLocalization from "../../model/custom/Localization";
import * as ModelPreference from "../../model/Preference";
import * as ApiInvoice from "../../api/custom/Invoice";
import * as ModelInvoice from "../../model/custom/Invoice";
import ControllerAlert from "../Alert";

export default class ControllerInvoiceUpload {
    // Variable
    private storeRole: ModelRole.Istore;
    private storageRoleData = React.useRef<ModelRole.Idata | undefined>(undefined);
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelCustomLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelCustomLocalization.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementViewMain = React.useRef<HTMLElement | null>(null);
    private elementBottom = React.useRef<HTMLElement | null>(null);
    private elementClientId = React.useRef<HTMLInputElement | null>(null);
    private elementFile = React.useRef<HTMLInputElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private validationForm = React.useRef<ModelInvoice.Idata | undefined>(undefined);
    private dataMain = React.useRef<ModelInvoice.Idata | undefined>(undefined);
    private language = React.useRef<string>("");

    getApiLoadingList: Record<string, boolean | undefined>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean | undefined>>>;

    getSelectOption: ModelInvoice.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelInvoice.IselectOption | undefined>>;

    getDataMain: ModelInvoice.Idata | undefined;
    setDataMain: React.Dispatch<React.SetStateAction<ModelInvoice.Idata | undefined>>;

    getValidationForm: ModelInvoice.Idata | undefined;
    setValidationForm: React.Dispatch<React.SetStateAction<ModelInvoice.Idata | undefined>>;

    getFilenameList: Record<string, string>;
    setFilenameList: React.Dispatch<React.SetStateAction<Record<string, string>>>;

    getUrlFile: string | undefined;
    setUrlFile: React.Dispatch<React.SetStateAction<string | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    getElementClientIdValue: string;
    setElementClientIdValue: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRole = new StoreRole();
        this.storageRoleData.current = this.storeRole.storageGet();
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean | undefined>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelInvoice.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getDataMain, setDataMain] = React.useState<ModelInvoice.Idata | undefined>(this.dataMain.current);
        this.getDataMain = getDataMain;
        this.setDataMain = setDataMain;

        const [getValidationForm, setValidationForm] = React.useState<ModelInvoice.Idata | undefined>(this.validationForm.current);
        this.getValidationForm = getValidationForm;
        this.setValidationForm = setValidationForm;

        const [getFilenameList, setFilenameList] = React.useState<Record<string, string>>({});
        this.getFilenameList = getFilenameList;
        this.setFilenameList = setFilenameList;

        const [getUrlFile, setUrlFile] = React.useState<string | undefined>(undefined);
        this.getUrlFile = getUrlFile;
        this.setUrlFile = setUrlFile;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;

        const [getElementClientIdValue, setElementClientIdValue] = React.useState<string>("");
        this.getElementClientIdValue = getElementClientIdValue;
        this.setElementClientIdValue = setElementClientIdValue;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_invoice_upload", () => {
                this.initializeHtmlElement();
            });

            this.apiSelectOption("upload");

            let validationFormType = "upload";

            this.dataMain.current = {} as ModelInvoice.Idata;

            this.setDataMain(this.dataMain.current);

            this.apiValidationForm(validationFormType);

            HelperSrc.storageEventListener("localization", () => {
                this.apiValidationForm(validationFormType);
            });

            ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
                document.addEventListener(
                    eventName,
                    (event: Event) => {
                        event.preventDefault();
                        event.stopPropagation();
                    },
                    false
                );
            });
        }, []);

        React.useEffect(() => {
            this.storageRoleData.current = this.storeRole.storageGet();
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
        }, [this.storageRoleData.current, this.storageRouteData.current, this.storageLocalizationData.current]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption("upload");
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    clickUpload = (tag: string): void => {
        const element = document.querySelector<HTMLInputElement>(tag);

        if (element) {
            element.click();
        }
    };

    returnFilename = (filelist: FileList | null): void => {
        this.setFilenameList((prev) => ({
            ...prev,
            file: filelist && filelist[0] ? filelist[0].name : ""
        }));
    };

    handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const fileList = event.dataTransfer.files;

        if (this.elementFile.current) {
            this.elementFile.current.files = fileList;

            this.setFilenameList((prev) => ({
                ...prev,
                file: fileList && fileList[0] ? fileList[0].name : ""
            }));
        }
    };

    clickSave = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        const element = event.target as HTMLElement;

        const checkForm = HelperSrc.checkForm(element, this.validationForm.current);

        if (!checkForm) {
            return;
        }

        const clientId = this.elementClientId.current ? this.elementClientId.current.value : "";
        const file = this.elementFile.current && this.elementFile.current.files ? this.elementFile.current.files[0] : "";

        const formData = new FormData();
        formData.append("client_id", clientId);
        formData.append("file", file);

        this.setApiLoadingList((prev) => ({
            ...prev,
            create: true
        }));

        ApiInvoice.uploadData(formData, (response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                create: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.controllerAlert.show("success", response.message.success, true);
        });
    };

    clickShow = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            previewFile: true
        }));

        const file = this.elementFile.current && this.elementFile.current.files ? this.elementFile.current.files[0] : "";

        const formData = new FormData();
        formData.append("file", file);

        ApiInvoice.previewFile(formData, (response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                previewFile: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            const decodedData = window.atob(response.data.result as unknown as string);
            const uint8Array = new Uint8Array(decodedData.length);

            for (let a = 0; a < decodedData.length; a++) {
                uint8Array[a] = decodedData.charCodeAt(a);
            }

            const blob = new Blob([uint8Array], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);

            this.setUrlFile(url);
        });
    };

    clickClose = (): void => {
        this.setUrlFile("");

        this.setApiLoadingList((prev) => ({
            ...prev,
            previewFile: undefined
        }));
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementBottom.current = document.querySelector<HTMLElement>(".bottom");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_invoice_upload");
            this.elementClientId.current = document.querySelector<HTMLInputElement>("[name='client_id']");
            this.elementFile.current = document.querySelector<HTMLInputElement>("[name='file']");
        }
    };

    private apiSelectOption = (mode: string): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiInvoice.selectOption(mode, (response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.setSelectOption(response.data.result as unknown as ModelInvoice.IselectOption);
        });
    };

    private apiValidationForm = (type: string): void => {
        ApiInvoice.validationForm(type, (response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.validationForm.current = response.data.result as unknown as ModelInvoice.Idata;
            this.setValidationForm(this.validationForm.current);

            if (this.elementBottom.current) {
                HelperSrc.validationFormHelper(this.elementBottom.current, this.validationForm.current);
            }
        });
    };
}
